<script setup>
import { ref, onMounted, onUnmounted, computed, defineProps, defineEmits, watch } from 'vue';
import { utcToLocal, timeSimplified } from '@/tools/TimeFunctions';
import unitImage from '@/assets/images/unit.png';

// Define props
const props = defineProps({
    unitData: Object,
    dataID: String,
    timeFormat: {
        type: String,
        default: '24hour'
    },
    subEntities: {
        type: Array,
        default: () => []
    },
    entityData: {
        type: Object,
        default: () => ({})
    },
    /** Device classes for the current entity (for "Assign to class" dropdown) */
    deviceClasses: {
        type: Array,
        default: () => []
    },
    /** Current class id for this unit (if any) */
    unitClassId: {
        type: String,
        default: null
    },
    /** Increment to reset the select (e.g. after create-class modal closes) */
    selectResetKey: {
        type: Number,
        default: 0
    },
    /** True while this specific unit's class assignment API call is in flight */
    classAssigning: {
        type: Boolean,
        default: false
    },
    /** True while classes are being reloaded from the API */
    classesLoading: {
        type: Boolean,
        default: false
    },
    /** Allow toggling which sub-entity groups this unit is linked to */
    canEditSubEntityLinks: {
        type: Boolean,
        default: false
    },
    /** True while this unit's sub-entity link update is in flight */
    entityLinkAssigning: {
        type: Boolean,
        default: false
    },
    canCreateEntity: {
        type: Boolean,
        default: false
    },
    canEditQuickMetrics: {
        type: Boolean,
        default: false
    },
    alertSeverity: {
        type: String,
        default: null
    },
    /** Optional source/field specs for class-aware quick metrics */
    quickMetricSpecs: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['assignToClass', 'createClass', 'toggleSubEntity', 'createEntity', 'editQuickMetrics', 'click']);

const classDropdownOpen = ref(false);
const entityDropdownOpen = ref(false);

// Ticks every minute to force time-based computeds to re-evaluate
const now = ref(Date.now());
let clockTimer = null;
onMounted(() => {
    clockTimer = setInterval(() => { now.value = Date.now(); }, 60_000);
});

/** Same rules as MainDashboard stats: deactivated → undeployed → online/offline */
const connectionStatus = computed(() => {
    void now.value; // reactive dependency so this re-runs each clock tick
    const d = props.unitData;
    if (!d) return 'undeployed';
    if (!d.active) return 'deactivated';
    if (!d.lastActivityUTC || d.lastActivityUTC === '') return 'undeployed';
    const t = timeSimplified(utcToLocal(d.lastActivityUTC, props.timeFormat));
    if (t === 'Current') return 'online';
    return 'offline';
});

const lastActivityLabel = computed(() => {
    void now.value;
    const d = props.unitData;
    if (!d?.lastActivityUTC) return 'No activity';
    return timeSimplified(utcToLocal(d.lastActivityUTC, props.timeFormat)) || 'No activity';
});

const friendlyNameDisplay = computed(() => props.unitData?.friendlyName || props.dataID);

const imageSrc = computed(() => (props.unitData?.image ? props.unitData.image : unitImage));

const currentClassName = computed(() => {
    if (!props.unitClassId || !props.deviceClasses?.length) return null;
    return props.deviceClasses.find((c) => c.id === props.unitClassId)?.name || null;
});

function hasSnapshotValue(value) {
    const rawValue = snapshotMetricRawValue(value);
    return rawValue !== undefined && rawValue !== null && rawValue !== '';
}

const snapshotMap = computed(() => {
    const snapshots = props.unitData?.snapshots;
    if (!snapshots || typeof snapshots !== 'object' || Array.isArray(snapshots)) return null;
    return snapshots;
});

function parseNumeric(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value.replace(',', '.'));
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
}

function clampToPercent(value) {
    if (!Number.isFinite(value)) return null;
    const normalized = value <= 1 && value >= 0 ? value * 100 : value;
    return Math.max(0, Math.min(100, Math.round(normalized)));
}

function formatMetricValue(value, unit = '', precision = 0) {
    if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A';
    const rounded = precision > 0
        ? value.toFixed(precision).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1')
        : `${Math.round(value)}`;
    return `${rounded}${unit ? ` ${unit}` : ''}`;
}

function parsePowerState(rawValue) {
    if (typeof rawValue === 'boolean') return rawValue;
    if (typeof rawValue === 'number') return rawValue > 0;
    if (typeof rawValue === 'string') {
        const normalized = rawValue.trim().toLowerCase();
        if (['on', 'enabled', 'running', 'active', 'true', '1'].includes(normalized)) return true;
        if (['off', 'disabled', 'stopped', 'inactive', 'false', '0'].includes(normalized)) return false;
    }
    return null;
}

function isSnapshotMetricEntry(value) {
    return value && typeof value === 'object' && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, 'value');
}

function snapshotMetricRawValue(value) {
    return isSnapshotMetricEntry(value) ? value.value : value;
}

function toMetricTone(kind, rawValue) {
    if (kind === 'soc') return 'blue';
    if (kind === 'watts') return 'violet';
    if (kind === 'signal') return 'amber';
    if (kind === 'power') {
        const parsed = parsePowerState(rawValue);
        return parsed ? 'green' : 'gray';
    }
    return 'gray';
}

function detectMetricKind(spec = {}) {
    const sourceToken = `${spec.source || ''}`.toLowerCase();
    const fieldToken = `${spec.field || ''}`.toLowerCase();
    const labelToken = `${spec.friendlyName || spec.label || ''}`.toLowerCase();
    const token = `${sourceToken}.${fieldToken} ${labelToken}`;
    const fieldAndLabelToken = `${fieldToken} ${labelToken}`;

    if (token.includes('soc') || token.includes('stateofcharge') || token.includes('state_of_charge')) return 'soc';
    if (token.includes('watt') || token.includes('powerw') || token.includes('activepower')) return 'watts';
    if (token.includes('signal') || token.includes('rssi') || token.includes('dbm') || token.includes('rsrp')) return 'signal';

    // Treat only explicit state-like power fields as boolean power metrics.
    // Avoid classifying numeric values like "battery voltage" as ON/OFF.
    const isPowerStateField =
        fieldToken === 'power' ||
        /(^|[\s_.-])(is[_-]?on|power[_-]?state|status|enabled|running|active)([\s_.-]|$)/.test(fieldAndLabelToken);
    if (isPowerStateField) return 'power';

    return 'generic';
}

function flattenTimeseriesStyleSpecs(specifications) {
    if (!specifications || typeof specifications !== 'object' || Array.isArray(specifications)) return [];
    const flattened = [];
    for (const [source, lines] of Object.entries(specifications)) {
        if (!Array.isArray(lines)) continue;
        for (const line of lines) {
            if (!line?.field) continue;
            flattened.push({
                source,
                field: line.field,
                friendlyName: line.friendlyName || line.label || '',
                unit: line.unit || '',
            });
        }
    }
    return flattened;
}

function normalizeQuickMetricSpecs(rawSpecs) {
    if (!Array.isArray(rawSpecs)) {
        return deriveSpecsFromSnapshots(rawSpecs);
    }
    return rawSpecs
        .map((spec) => ({
            source: typeof spec === 'string'
                ? (spec.includes('.') ? spec.slice(0, spec.indexOf('.')).trim() : '')
                : (typeof spec?.source === 'string' ? spec.source : ''),
            field: typeof spec === 'string'
                ? (spec.includes('.') ? spec.slice(spec.indexOf('.') + 1).trim() : '')
                : (typeof spec?.field === 'string' ? spec.field : ''),
            friendlyName: typeof spec?.friendlyName === 'string'
                ? spec.friendlyName
                : (typeof spec?.label === 'string'
                    ? spec.label
                    : (typeof spec === 'string'
                        ? toMetricLabel(spec.includes('.') ? spec.slice(spec.indexOf('.') + 1) : spec)
                        : '')),
            unit: typeof spec?.unit === 'string' ? spec.unit : '',
        }))
        .filter((spec) => spec.field);
}

function toMetricLabel(field = '') {
    const token = String(field || '').trim();
    if (!token) return 'Metric';
    return token
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function deriveSpecsFromSnapshots(snapshots) {
    if (!snapshots || typeof snapshots !== 'object' || Array.isArray(snapshots)) return [];

    const specs = [];
    const sortedKeys = Object.keys(snapshots).sort((a, b) => a.localeCompare(b));
    for (const key of sortedKeys) {
        if (!key.includes('.')) continue;
        const firstDot = key.indexOf('.');
        const source = key.slice(0, firstDot).trim();
        const field = key.slice(firstDot + 1).trim();
        if (!source || !field) continue;

        const snapshotValue = snapshots[key];
        specs.push({
            source,
            field,
            friendlyName: (typeof snapshotValue?.friendlyName === 'string' && snapshotValue.friendlyName.trim())
                ? snapshotValue.friendlyName
                : toMetricLabel(field),
            unit: typeof snapshotValue?.unit === 'string' ? snapshotValue.unit : '',
            snapshotDerived: true,
            mapAnimationRule: (snapshotValue?.mapAnimationRule && typeof snapshotValue.mapAnimationRule === 'object' && !Array.isArray(snapshotValue.mapAnimationRule))
                ? snapshotValue.mapAnimationRule
                : null,
        });
    }
    return specs;
}

function resolveConfiguredRawValue(spec = {}) {
    if (!spec.field) return null;
    const snapshots = snapshotMap.value;
    if (!snapshots) return null;

    const sourceName = (spec.source || '').trim();
    const fieldPath = String(spec.field || '').trim();
    if (!fieldPath) return null;

    if (sourceName) {
        const nested = snapshots[sourceName]?.[fieldPath];
        if (hasSnapshotValue(nested)) return snapshotMetricRawValue(nested);
        const snapshotKey = `${sourceName}.${fieldPath}`;
        const value = snapshots[snapshotKey];
        return hasSnapshotValue(value) ? snapshotMetricRawValue(value) : null;
    }

    const direct = snapshots[fieldPath];
    if (hasSnapshotValue(direct)) return snapshotMetricRawValue(direct);

    // Backward compatibility for specs that omitted source:
    // match "<anySource>.<field>" in the snapshots map.
    const suffix = `.${fieldPath}`;
    for (const [key, value] of Object.entries(snapshots)) {
        if (!key.endsWith(suffix)) continue;
        if (hasSnapshotValue(value)) return snapshotMetricRawValue(value);
    }

    return null;
}

function buildMetricFromSpec(spec, idx) {
    const rawValue = resolveConfiguredRawValue(spec);
    if (rawValue === null) return null;

    const kind = detectMetricKind(spec);
    const tone = spec?.snapshotDerived ? 'gray' : toMetricTone(kind, rawValue);
    const label = spec.friendlyName || spec.label || spec.field || `Metric ${idx + 1}`;
    const unit = spec.unit || '';

    if (kind === 'power') {
        const parsed = parsePowerState(rawValue);
        if (parsed !== null) {
            return {
                key: `${spec.source || 'root'}.${spec.field || idx}`,
                kind,
                label,
                value: parsed,
                displayValue: parsed ? 'ON' : 'OFF',
                tone,
            };
        }
        // Value doesn't parse as boolean (e.g. "Available", "Charging") — fall through to string display
    }

    const numeric = parseNumeric(rawValue);
    if (numeric === null) {
        return {
            key: `${spec.source || 'root'}.${spec.field || idx}`,
            kind,
            label,
            value: rawValue,
            displayValue: String(rawValue),
            tone,
        };
    }

    if (kind === 'soc') {
        const percent = clampToPercent(numeric);
        if (percent === null) return null;
        return {
            key: `${spec.source || 'root'}.${spec.field || idx}`,
            kind,
            label,
            value: percent,
            displayValue: `${percent}%`,
            tone,
        };
    }

    if (kind === 'signal' && !unit) {
        const isDbm = numeric < 0;
        return {
            key: `${spec.source || 'root'}.${spec.field || idx}`,
            kind,
            label,
            value: numeric,
            displayValue: isDbm ? formatMetricValue(numeric, 'dBm', 0) : formatMetricValue(numeric, '%', 0),
            tone,
        };
    }

    return {
        key: `${spec.source || 'root'}.${spec.field || idx}`,
        kind,
        label,
        value: numeric,
        displayValue: formatMetricValue(numeric, unit, 2),
        tone,
    };
}

const configuredMetrics = computed(() => {
    const snapshots = snapshotMap.value;
    if (!snapshots || Object.keys(snapshots).length === 0) return [];

    const classSpecs = normalizeQuickMetricSpecs(props.quickMetricSpecs);
    const unitSpecsRaw =
        props.unitData?.quickMetrics ||
        props.unitData?.quickMetricSpecs ||
        props.unitData?.dashboard?.quickMetrics ||
        flattenTimeseriesStyleSpecs(props.unitData?.quickMetricSpecifications || props.unitData?.specifications);
    const unitSpecs = normalizeQuickMetricSpecs(unitSpecsRaw);
    const derivedSnapshotSpecs = deriveSpecsFromSnapshots(snapshots);

    const specsToUse =
        classSpecs.length > 0
            ? classSpecs
            : (unitSpecs.length > 0 ? unitSpecs : derivedSnapshotSpecs);
    if (specsToUse.length === 0) return [];

    return specsToUse
        .map((spec, idx) => buildMetricFromSpec(spec, idx))
        .filter(Boolean);
});

const normalizedMetrics = computed(() => configuredMetrics.value);

const visibleMetrics = computed(() => normalizedMetrics.value);

const socMetric = computed(() => normalizedMetrics.value.find((m) => m.kind === 'soc') || null);

const socBarClass = computed(() => {
    const value = socMetric.value?.value;
    if (value === undefined || value === null) return 'bg-mc-steel';
    if (value > 60) return 'bg-green-500';
    if (value > 30) return 'bg-amber-500';
    return 'bg-mc-red';
});

const statusBadgeText = computed(() => {
    switch (connectionStatus.value) {
        case 'deactivated':
            return 'Deactivated';
        case 'undeployed':
            return 'Undeployed';
        case 'online':
            return 'Online';
        default:
            return lastActivityLabel.value;
    }
});


/** Status dot on avatar */
const statusDotClass = computed(() => {
    switch (connectionStatus.value) {
        case 'deactivated':
            return 'bg-ink-muted';
        case 'undeployed':
            return 'bg-mc-steel';
        case 'online':
            return 'bg-green-500';
        default:
            return 'bg-mc-steel';
    }
});

const alertIconConfig = computed(() => {
    if (props.alertSeverity === 'critical') {
        return {
            kind: 'critical',
            text: '!',
            classes: 'bg-mc-red text-mc-white',
            title: 'Critical alert',
        };
    }
    if (props.alertSeverity === 'warning') {
        return {
            kind: 'warning',
            title: 'Warning alert',
        };
    }
    return null;
});

function selectClass(classId) {
    classDropdownOpen.value = false;
    if (classId === 'createClass') {
        emit('createClass', props.dataID);
        return;
    }
    emit('assignToClass', classId || null);
}

function toggleClassDropdown() {
    entityDropdownOpen.value = false;
    classDropdownOpen.value = !classDropdownOpen.value;
}

function toggleEntityDropdown() {
    classDropdownOpen.value = false;
    entityDropdownOpen.value = !entityDropdownOpen.value;
}

function emitToggleSubEntity(subEntityId) {
    emit('toggleSubEntity', subEntityId);
}

function emitCreateEntity() {
    entityDropdownOpen.value = false;
    emit('createEntity', props.dataID);
}

function emitEditQuickMetrics() {
    emit('editQuickMetrics', props.dataID);
}

function subEntityIsLinked(subEntityId) {
    return !!(props.unitData?.entities && props.unitData.entities.includes(subEntityId));
}

// Sub-entity rows this unit is linked to (for summary + menu state)
const unitSubEntities = computed(() => {
    if (!props.unitData?.entities || !props.subEntities || !props.entityData) return [];

    const filtered = props.subEntities
        .filter(subEntityId => props.unitData.entities.includes(subEntityId))
        .map(subEntityId => ({
            id: subEntityId,
            name: props.entityData[subEntityId]?.name || subEntityId,
            ...props.entityData[subEntityId]
        }))
        .filter(entity => entity.name);

    return filtered;
});

const entityDropdownSummary = computed(() => {
    const list = unitSubEntities.value;
    if (!list.length) return '+ Entity';
    if (list.length === 1) return list[0].name;
    return `${list[0].name} +${list.length - 1}`;
});

// Close dropdowns when clicking anywhere outside
function onDocumentClick() {
    classDropdownOpen.value = false;
    entityDropdownOpen.value = false;
}

watch([classDropdownOpen, entityDropdownOpen], ([c, e]) => {
    if (c || e) {
        setTimeout(() => document.addEventListener('click', onDocumentClick), 0);
    } else {
        document.removeEventListener('click', onDocumentClick);
    }
});

onUnmounted(() => {
    clearInterval(clockTimer);
    document.removeEventListener('click', onDocumentClick);
});

function metricValueClass(metric) {
    switch (metric.tone) {
        case 'blue':   return 'text-mc-blue dark:text-mc-blue';
        case 'green':  return 'text-green-500 dark:text-green-400';
        case 'amber':  return 'text-amber-500 dark:text-amber-400';
        case 'violet': return 'text-amber-500 dark:text-amber-400';
        default:       return 'text-mesh-text-primary';
    }
}

</script>

<template>
    <div
      tabindex="0"
      role="button"
      @click="emit('click')"
      @keydown.enter.prevent="emit('click')"
      @keydown.space.prevent="emit('click')"
      class="relative shrink-0 bg-mesh-surface-card border border-mc-steel/50 dark:border-line-on-dark rounded-mc-md cursor-pointer mx-2 my-2 transition-all duration-180 hover:border-mc-steel/80 hover:-translate-y-0.5 hover:shadow-mc-md focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
      :class="{ 'z-[1]': classDropdownOpen || entityDropdownOpen }"
    >
        <div class="absolute inset-0 overflow-hidden rounded-mc-md pointer-events-none z-[2]" aria-hidden="true">
            <div class="absolute top-0 left-0 bottom-0 w-[3px] bg-mc-red"></div>
        </div>
        <!-- Zone 1: Top row — image + identity -->
        <div class="flex overflow-hidden rounded-t-mc-md">
            <!-- Image zone -->
            <div class="relative w-20 flex-shrink-0 flex items-center justify-center min-h-[88px]">
                <img
                    v-if="imageSrc !== unitImage"
                    class="w-full h-full object-cover"
                    :src="imageSrc"
                    :alt="friendlyNameDisplay || dataID"
                />
                <svg v-else class="w-10 h-10 text-mc-steel" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                </svg>
            </div>

            <!-- Identity zone -->
            <div class="flex-1 min-w-0 px-3 py-3 border-b border-mc-steel/30 dark:border-line-on-dark">
                <!-- Status row -->
                <div class="flex items-center justify-between gap-2 mb-1.5">
                    <div class="flex items-center gap-1.5 min-w-0">
                        <span class="w-[7px] h-[7px] rounded-full flex-shrink-0" :class="statusDotClass" aria-hidden="true"></span>
                        <span class="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-ink-muted dark:text-on-dark-muted truncate">{{ statusBadgeText }}</span>
                    </div>
                    <div class="flex items-center gap-1 flex-shrink-0">
                        <span
                            v-if="alertIconConfig && alertIconConfig.kind === 'critical'"
                            class="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-mc-red text-mc-white text-[0.65rem] font-black leading-none"
                            :title="alertIconConfig.title"
                            aria-label="Critical alert"
                        >!</span>
                        <span
                            v-else-if="alertIconConfig && alertIconConfig.kind === 'warning'"
                            class="inline-flex items-center justify-center w-[18px] h-[18px]"
                            :title="alertIconConfig.title"
                            aria-label="Warning alert"
                        >
                            <svg class="w-4 h-4" viewBox="0 0 20 20" aria-hidden="true">
                                <path d="M10 2.6L18 16.8a1 1 0 01-.87 1.5H2.87a1 1 0 01-.87-1.5L10 2.6z" fill="rgb(251 191 36)" stroke="rgb(120 53 15)" stroke-width="1.2" stroke-linejoin="round"/>
                                <path d="M10 7.2v4.8" stroke="rgb(120 53 15)" stroke-width="1.8" stroke-linecap="round"/>
                                <circle cx="10" cy="14.5" r="1.1" fill="rgb(120 53 15)"/>
                            </svg>
                        </span>
                    </div>
                </div>
                <!-- Device name — Saira display font -->
                <h2 class="font-display font-black text-[0.92rem] leading-[1.1] text-mc-ink dark:text-on-dark truncate">{{ friendlyNameDisplay }}</h2>
                <!-- DataID — mono, subtle -->
                <p class="font-mono text-[0.58rem] tracking-[0.06em] text-ink-muted dark:text-on-dark-muted mt-1 truncate">{{ dataID }}</p>
            </div>
        </div>

        <!-- Zone 2: Metrics row -->
        <div class="px-3 py-2.5 flex items-end gap-3 flex-wrap">
            <template v-if="visibleMetrics.length > 0">
                <template v-for="(metric, idx) in visibleMetrics" :key="metric.key">
                    <div v-if="idx > 0" class="w-px self-stretch bg-mc-steel/40 dark:bg-white/[0.10] shrink-0" aria-hidden="true"></div>
                    <div class="flex flex-col gap-0.5 shrink-0">
                        <span class="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-ink-muted dark:text-on-dark-muted leading-none">{{ metric.label }}</span>
                        <span class="font-mono text-[0.85rem] font-medium leading-none" :class="metricValueClass(metric)">{{ metric.displayValue }}</span>
                    </div>
                </template>
                <div v-if="socMetric" class="flex-1 min-w-[48px] self-end">
                    <div class="h-[3px] rounded-full bg-mc-steel/20 dark:bg-white/[0.06] overflow-hidden">
                        <div class="h-full rounded-full transition-[width] duration-300" :class="socBarClass" :style="{ width: `${socMetric.value}%` }"></div>
                    </div>
                </div>
            </template>
            <p v-else class="font-mono text-[0.6rem] text-ink-muted dark:text-on-dark-muted">No telemetry</p>
            <div v-if="canEditQuickMetrics" class="ml-auto flex-shrink-0" @click.stop>
                <button
                    type="button"
                    @click="emitEditQuickMetrics"
                    class="inline-flex items-center justify-center h-5 w-5 rounded-[6px] border border-mc-steel dark:border-line-on-dark text-ink-muted dark:text-on-dark-muted hover:bg-mc-cloud dark:hover:bg-mc-ink/30 transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                    title="Edit quick metrics"
                    aria-label="Edit quick metrics"
                >
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536M9 11l6.768-6.768a2.5 2.5 0 113.536 3.536L12.536 14.536a4 4 0 01-1.757 1.01L7 16l.454-3.779A4 4 0 018.464 10.464z"/>
                    </svg>
                </button>
            </div>
        </div>

        <!-- Zone 3: Tags (entity + class) -->
        <div class="px-3 pb-2.5 pt-2 flex flex-wrap items-center gap-1.5 border-t border-mc-steel/30 dark:border-line-on-dark">

            <!-- Entity dropdown -->
            <div class="relative" @click.stop>
                <button
                    type="button"
                    @click="toggleEntityDropdown"
                    :disabled="entityLinkAssigning"
                    class="inline-flex items-center gap-1 min-h-[36px] sm:min-h-0 px-2 py-1 rounded-full text-[0.65rem] font-semibold font-body transition-colors duration-160 touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                    :class="unitSubEntities.length
                        ? 'bg-mc-blue/10 border border-mc-blue/30 text-mc-blue hover:bg-mc-blue/15'
                        : 'border border-dashed border-mc-steel/40 text-ink-muted dark:text-on-dark-muted hover:bg-mc-cloud dark:hover:bg-mc-charcoal/80'"
                >
                    <svg v-if="entityLinkAssigning" class="w-2.5 h-2.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                    </svg>
                    <template v-else>
                        {{ entityDropdownSummary }}
                        <svg class="w-2 h-2 opacity-60 flex-shrink-0" fill="none" viewBox="0 0 10 6">
                            <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </template>
                </button>
                <div
                    v-if="entityDropdownOpen"
                    class="absolute left-0 top-full mt-1 z-50 min-w-[180px] max-h-[240px] overflow-y-auto bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm shadow-mc-sm py-1 text-sm"
                >
                    <button
                        v-for="seid in subEntities"
                        :key="seid"
                        type="button"
                        :disabled="!canEditSubEntityLinks"
                        :aria-pressed="subEntityIsLinked(seid)"
                        @click="canEditSubEntityLinks && emitToggleSubEntity(seid)"
                        class="w-full text-left px-3 py-1.5 hover:bg-mc-cloud dark:hover:bg-mc-ink/30 text-mc-ink dark:text-on-dark font-body flex items-center gap-2 disabled:opacity-50 disabled:pointer-events-none transition-colors duration-160 focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-[2px] focus-visible:ring-mc-blue/40"
                    >
                        <span
                            class="inline-flex w-3.5 h-3.5 rounded border shrink-0 items-center justify-center text-[10px] font-bold"
                            :class="subEntityIsLinked(seid)
                                ? 'bg-mc-blue border-mc-blue text-mc-white'
                                : 'border-mc-steel dark:border-line-on-dark'"
                        >{{ subEntityIsLinked(seid) ? '✓' : '' }}</span>
                        <span class="truncate">{{ entityData[seid]?.name || seid }}</span>
                    </button>
                    <div
                        v-if="canCreateEntity"
                        class="mt-1 pt-1"
                        :class="subEntities.length > 0 ? 'border-t border-mc-steel/50 dark:border-line-on-dark' : ''"
                    >
                        <button
                            type="button"
                            @click="emitCreateEntity"
                            class="w-full text-left px-3 py-1.5 font-body text-mc-blue hover:bg-mc-blue/5 dark:hover:bg-mc-blue/20 transition-colors duration-160"
                        >
                            + Entity
                        </button>
                    </div>
                </div>
            </div>

            <!-- Class dropdown -->
            <div class="relative" @click.stop>
                <button
                    type="button"
                    @click="toggleClassDropdown"
                    :disabled="classAssigning || classesLoading"
                    class="inline-flex items-center gap-1 min-h-[36px] sm:min-h-0 px-2 py-1 rounded-full text-[0.65rem] font-semibold font-body transition-colors duration-160 touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                    :class="currentClassName
                        ? 'bg-mc-blue/10 border border-mc-blue/30 text-mc-blue hover:bg-mc-blue/15'
                        : 'border border-dashed border-mc-steel/40 text-ink-muted dark:text-on-dark-muted hover:bg-mc-cloud dark:hover:bg-mc-charcoal/80'"
                >
                    <svg v-if="classAssigning || classesLoading" class="w-2.5 h-2.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                    </svg>
                    <template v-else>
                        {{ currentClassName || '+ Class' }}
                        <svg class="w-2 h-2 opacity-60 flex-shrink-0" fill="none" viewBox="0 0 10 6">
                            <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </template>
                </button>
                <div
                    v-if="classDropdownOpen"
                    class="absolute left-0 top-full mt-1 z-50 min-w-[140px] bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm shadow-mc-sm py-1 text-sm"
                >
                    <button
                        v-if="currentClassName"
                        type="button"
                        @click="selectClass(null)"
                        class="w-full text-left px-3 py-1.5 font-body text-ink-muted dark:text-on-dark-muted hover:bg-mc-cloud dark:hover:bg-mc-ink/30 transition-colors duration-160"
                    >
                        Remove class
                    </button>
                    <button
                        v-for="c in deviceClasses"
                        :key="c.id"
                        type="button"
                        @click="selectClass(c.id)"
                        class="w-full text-left px-3 py-1.5 font-body text-mc-ink dark:text-on-dark hover:bg-mc-cloud dark:hover:bg-mc-ink/30 transition-colors duration-160"
                        :class="{ 'font-semibold text-mc-blue dark:text-mc-blue': c.id === unitClassId }"
                    >
                        {{ c.name }}
                    </button>
                    <div class="border-t border-mc-steel/50 dark:border-line-on-dark mt-1 pt-1">
                        <button
                            type="button"
                            @click="selectClass('createClass')"
                            class="w-full text-left px-3 py-1.5 font-body text-mc-blue hover:bg-mc-blue/5 dark:hover:bg-mc-blue/20 transition-colors duration-160"
                        >
                            + New Class
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>